import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import usePageMeta from '../hooks/usePageMeta';

// The page behind the Yes/No links in the season email and text, at
// /season/:token/yes and /season/:token/no. No login: the token in the link is
// the credential (server/services/seasonRespond.js).
//
// /no records "declined" as soon as the page loads (one tap) and says so.
// /yes shows the choices, prefilled from what is on record or the customer's
// card, and records "confirmed" on Confirm. Either page can be opened again:
// answering again changes the answer, and the page says what is on record.
// An unknown or expired link shows "This link has expired" with the
// company's phone when it is known.
//
// noindex here, an X-Robots-Tag from netlify.toml, and a Disallow in
// robots.txt: the path is a private link, never a page to be found.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export const PREFERENCE_LABELS = { seasonal: 'Seasonal', on_call: 'On-call' };
export const PREFERENCE_HELP = {
  seasonal: 'We clear your driveway every time it snows enough.',
  on_call: 'We come when you ask.',
};

// The salt options, each mapped to customers.salt_preference: Always is
// 'always' (salt every visit), When icy is 'as_needed', No is 'never'.
export const SALT_OPTIONS = [
  { value: 'always', label: 'Always', help: 'Salt every visit' },
  { value: 'as_needed', label: 'When icy', help: 'Salt when it is icy' },
  { value: 'never', label: 'No', help: null },
];
const SALT_RECORD = { always: 'salt every visit', as_needed: 'salt when icy', never: 'no salt' };

// The salt choice from the server's choices. A server from before the three
// options sent only wantsSalt, whose Yes was "when icy".
export function saltChoice(choices) {
  const c = choices || {};
  if (SALT_RECORD[c.saltPreference]) return c.saltPreference;
  return c.wantsSalt ? 'as_needed' : 'never';
}

// The form the Yes page starts from, and what Confirm posts.
export function formFrom(choices) {
  const c = choices || {};
  return {
    servicePreference: c.servicePreference || 'seasonal',
    saltPreference: saltChoice(c),
    snowTierId: c.snowTierId ?? null,
  };
}

// What is on record, in a sentence.
export function recordText(data) {
  if (!data || data.status === 'pending') return null;
  if (data.status === 'declined') return 'On record: no service this winter.';
  const c = data.choices || {};
  const size = (data.sizes || []).find((s) => s.id === c.snowTierId);
  const parts = [PREFERENCE_LABELS[c.servicePreference] || 'Seasonal', SALT_RECORD[saltChoice(c)]];
  if (size) parts.push(size.name);
  return `On record: yes, ${parts.join(', ')}.`;
}

const telHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

function Phone({ phone }) {
  if (!phone) return <>your snow removal company</>;
  return <a href={telHref(phone)}>{phone}</a>;
}

function SeasonRespond() {
  const { token, answer } = useParams();
  const isNo = answer === 'no';
  const [data, setData] = useState(null);
  const [expired, setExpired] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(formFrom(null));
  // React StrictMode runs effects twice in development; the No answer must be
  // sent once.
  const sentNo = useRef(false);

  usePageMeta({ title: data?.companyName ? `${data.companyName}: this winter` : 'Snow removal this winter', robots: 'noindex' });

  const url = `${API_URL}/api/season/respond/${encodeURIComponent(token)}`;

  const failed = (err) => {
    const body = err.response?.data;
    if (body?.expired) setExpired({ companyPhone: body.companyPhone || null });
    else if (err.response?.status === 429) setError('Too many tries. Please wait a while, or text or call us.');
    else setError('Something went wrong. Please try again.');
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (isNo) {
          if (sentNo.current) return;
          sentNo.current = true;
          const res = await axios.post(url, { answer: 'no' });
          if (!cancelled) { setData(res.data); setDone(true); }
          return;
        }
        const res = await axios.get(url);
        if (cancelled) return;
        setData(res.data);
        setForm(formFrom(res.data.choices));
      } catch (err) {
        if (!cancelled) failed(err);
      }
    };
    if (answer === 'yes' || answer === 'no') load();
    else setExpired({ companyPhone: null });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, answer]);

  const confirm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await axios.post(url, { answer: 'yes', ...form });
      setData(res.data);
      setForm(formFrom(res.data.choices));
      setDone(true);
    } catch (err) {
      failed(err);
    } finally {
      setSaving(false);
    }
  };

  const shell = (children) => (
    <div className="season-page"><style>{styles}</style><div className="season-card">{children}</div></div>
  );

  if (expired) {
    return shell(
      <p className="season-lead" data-testid="season-expired">
        This link has expired. Text or call <Phone phone={expired.companyPhone} />.
      </p>
    );
  }
  if (error && !data) return shell(<p className="season-lead" role="alert">{error}</p>);
  if (!data) return shell(<div className="season-spinner" aria-label="Loading" />);

  const header = (
    <header className="season-header">
      <h1 className="season-title">{data.companyName}</h1>
      <p className="season-kicker">Snow removal, season {data.seasonKey}</p>
    </header>
  );

  if (isNo) {
    return shell(<>
      {header}
      <p className="season-lead" data-testid="season-declined">
        Thanks{data.firstName ? `, ${data.firstName}` : ''}, we have taken you off the list for this winter.
      </p>
      <p>Changed your mind? <Phone phone={data.companyPhone} /></p>
    </>);
  }

  if (done) {
    const record = recordText(data);
    return shell(<>
      {header}
      <p className="season-lead" data-testid="season-confirmed">You&apos;re on the list{data.firstName ? `, ${data.firstName}` : ''}.</p>
      {record && <p className="season-record">{record}</p>}
      {data.sizeFlagged && <p className="season-muted">We will check the size of your driveway and confirm it with you.</p>}
      {data.ratesLink && <p><a href={data.ratesLink}>See our rates</a></p>}
      <p className="season-muted">Questions or a change? Text or call <Phone phone={data.companyPhone} />.</p>
    </>);
  }

  const record = recordText(data);
  const radio = (name, value, checked, onChange, label, help) => (
    <label className={`season-option${checked ? ' is-checked' : ''}`} key={`${name}-${value}`}>
      <input type="radio" name={name} value={String(value)} checked={checked} onChange={onChange} />
      <span>
        <span className="season-option-label">{label}</span>
        {help && <span className="season-option-help">{help}</span>}
      </span>
    </label>
  );

  return shell(<>
    {header}
    <p className="season-lead">{data.firstName ? `Hi ${data.firstName}, ` : ''}do you want snow removal this winter?</p>
    {record && <p className="season-record" data-testid="season-record">{record} Answering again changes it.</p>}
    <form onSubmit={confirm}>
      <fieldset className="season-fieldset">
        <legend>Service</legend>
        {['seasonal', 'on_call'].map((v) => radio('servicePreference', v, form.servicePreference === v,
          () => setForm({ ...form, servicePreference: v }), PREFERENCE_LABELS[v], PREFERENCE_HELP[v]))}
      </fieldset>
      <fieldset className="season-fieldset">
        <legend>Salt</legend>
        {SALT_OPTIONS.map((o) => radio('saltPreference', o.value, form.saltPreference === o.value,
          () => setForm({ ...form, saltPreference: o.value }), o.label, o.help))}
      </fieldset>
      {data.sizes.length > 0 && (
        <fieldset className="season-fieldset">
          <legend>Driveway size</legend>
          {data.sizes.map((s) => radio('snowTierId', s.id, form.snowTierId === s.id,
            () => setForm({ ...form, snowTierId: s.id }), s.name, s.description))}
        </fieldset>
      )}
      {error && <p className="season-error" role="alert">{error}</p>}
      <button type="submit" className="season-button" disabled={saving}>{saving ? 'Saving...' : 'Confirm'}</button>
    </form>
    {data.ratesLink && <p className="season-muted"><a href={data.ratesLink}>See our rates</a></p>}
    <p className="season-muted">Not this winter? <a href={`/season/${encodeURIComponent(token)}/no`}>No thanks</a></p>
  </>);
}

const styles = `
  .season-page {
    min-height: 100vh; background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding: 16px; display: flex; justify-content: center; align-items: flex-start; box-sizing: border-box;
  }
  .season-card {
    background: #fff; border-radius: 16px; width: 100%; max-width: 520px; padding: 24px 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3); box-sizing: border-box; color: #1f2937; line-height: 1.5;
  }
  .season-header { text-align: center; margin-bottom: 16px; }
  .season-title { font-size: 1.5rem; color: #1e3a5f; margin: 0 0 4px; overflow-wrap: anywhere; }
  .season-kicker { color: #6b7280; margin: 0; font-size: 0.95rem; }
  .season-lead { font-size: 1.1rem; margin: 0 0 12px; }
  .season-record { background: #eff6ff; border-radius: 10px; padding: 10px 12px; }
  .season-muted { color: #6b7280; font-size: 0.9rem; }
  .season-error { color: #b91c1c; }
  .season-fieldset { border: none; padding: 0; margin: 16px 0 0; }
  .season-fieldset legend { font-weight: 600; color: #1e3a5f; margin-bottom: 6px; }
  .season-option {
    display: flex; gap: 10px; align-items: flex-start; padding: 10px 12px; margin-bottom: 6px;
    border: 1px solid #e5e7eb; border-radius: 10px; cursor: pointer;
  }
  .season-option.is-checked { border-color: #2563eb; background: #eff6ff; }
  .season-option input { margin-top: 4px; }
  .season-option-label { display: block; font-weight: 600; }
  .season-option-help { display: block; color: #6b7280; font-size: 0.85rem; }
  .season-button {
    display: block; width: 100%; margin-top: 20px; padding: 14px; border: none; border-radius: 10px;
    background: #2563eb; color: #fff; font-weight: 600; font-size: 1rem; cursor: pointer;
  }
  .season-button:disabled { opacity: 0.7; cursor: default; }
  .season-spinner { width: 32px; height: 32px; margin: 24px auto; border: 3px solid #e5e7eb; border-top-color: #2563eb; border-radius: 50%; animation: season-spin 0.8s linear infinite; }
  @keyframes season-spin { to { transform: rotate(360deg); } }
  @media (min-width: 640px) { .season-page { padding: 32px 20px; } .season-card { padding: 32px; } }
`;

export default SeasonRespond;
